<template>
  <Transition name="slide-fade" appear>
    <div v-if="visible" class="modal">
      <div class="modal-content">
        <div class="modal-step-1">
          <div class="modal-header">
            <span class="modal-header-txt">{{ $t('program_settings') }}</span>
            <span class="modal-header-description">{{ $t('program_settings_here') }}</span>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{ $t('published') }}
              </span>
              <span class="setting-text">
                {{ $t('published_or_draft') }}
              </span>
            </div>
            <div class="setting-right">
              <Switch v-model="settings.isPublish" @update:modelValue="handlePublishChange" />
            </div>
          </div>

          <div v-if="settings.isPublish" class="setting-wrapper language-options mt-5">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('published_languages_program')}}
              </span>
            </div>
            <div class="setting-right language-switches">
              <div class="language-switch">
                <span class="language-label">{{$t('turkish_title')}}</span>
                <Switch v-model="publishedLanguages.tr" @update:modelValue="handleLanguageChange('tr')" />
              </div>
              <div class="language-switch">
                <span class="language-label">{{$t('english_title')}}</span>
                <Switch v-model="publishedLanguages.en" @update:modelValue="handleLanguageChange('en')" />
              </div>
            </div>
          </div>

          <div v-if="settings.isPublish" class="language-info-note">
            <svg width="12" height="12" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fill-rule="evenodd" clip-rule="evenodd"
                    d="M7 13.25C10.4518 13.25 13.25 10.4518 13.25 7C13.25 3.54822 10.4518 0.75 7 0.75C3.54822 0.75 0.75 3.54822 0.75 7C0.75 10.4518 3.54822 13.25 7 13.25ZM8.11584 10.2086L8.21565 9.8006C8.16399 9.82486 8.0807 9.85258 7.96649 9.88414C7.85197 9.9157 7.7489 9.93177 7.65831 9.93177C7.46536 9.93177 7.32952 9.90014 7.25065 9.83657C7.17236 9.773 7.13331 9.65342 7.13331 9.47827C7.13331 9.40887 7.14509 9.30542 7.16973 9.17003C7.19361 9.03373 7.22108 8.91261 7.25161 8.80666L7.62419 7.48759C7.66068 7.36653 7.68571 7.23344 7.69916 7.08818C7.71292 6.94325 7.71932 6.84185 7.71932 6.78429C7.71932 6.50614 7.62182 6.28041 7.42676 6.10629C7.2317 5.93229 6.95393 5.84529 6.59396 5.84529C6.39365 5.84529 6.18188 5.88088 5.95776 5.952C5.73363 6.02294 5.49933 6.1084 5.25421 6.2082L5.15415 6.6165C5.22719 6.58949 5.31419 6.56043 5.41598 6.53034C5.51732 6.50038 5.61674 6.48489 5.71347 6.48489C5.91096 6.48489 6.04399 6.51856 6.1137 6.58488C6.18342 6.65139 6.21844 6.7697 6.21844 6.93883C6.21844 7.03236 6.20736 7.13626 6.18438 7.24919C6.16172 7.36282 6.13342 7.48298 6.10013 7.6098L5.72595 8.93419C5.69266 9.07336 5.66834 9.19787 5.65304 9.30843C5.63786 9.41886 5.63057 9.52724 5.63057 9.63262C5.63057 9.90482 5.73114 10.1292 5.93222 10.3063C6.13329 10.4826 6.41523 10.5714 6.77769 10.5714C7.01372 10.5714 7.22088 10.5406 7.39917 10.4785C7.57727 10.4167 7.81644 10.3268 8.11584 10.2086ZM8.04946 4.8502C8.22352 4.68882 8.31014 4.49254 8.31014 4.26272C8.31014 4.03341 8.22365 3.83675 8.04946 3.67331C7.87584 3.51032 7.66657 3.42857 7.4219 3.42857C7.17646 3.42857 6.96635 3.51013 6.79107 3.67331C6.61579 3.83675 6.52796 4.03334 6.52796 4.26272C6.52796 4.49254 6.61579 4.68875 6.79107 4.8502C6.96667 5.01217 7.17639 5.09321 7.4219 5.09321C7.66664 5.09321 7.87584 5.01217 8.04946 4.8502Z"
                    fill="#98A2B3"/>
            </svg>
            <span>{{$t('language_info')}}</span>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{ $t('program_image') }}
              </span>
              <span class="setting-text">
                {{ $t('program_card_image') }}
              </span>
            </div>
            <div class="setting-right">
              <FileDrop :ratio="'1920*1920'" :on-file-selected="handleProgramImage" :selected-file="settings.avatarUrl" :attach="settings.avatar" :on-delete="handleProgramImageDelete" />
            </div>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('start_at')}}
              </span>
              <span class="setting-text">
                {{$t('program_available_from')}}
              </span>
            </div>
            <div class="setting-right">
              <input type="datetime-local" class="form-input" @input="handleStartDate" :value="getStartDate" />
            </div>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('finish_at')}}
              </span>
              <span class="setting-text">
                {{$t('program_until_from')}}
              </span>
            </div>
            <div class="setting-right">
              <input type="datetime-local" class="form-input" @input="handleFinishDate" :value="getFinishDate" />
            </div>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('allow_retake')}}
              </span>
              <span class="setting-text">
                {{$t('respondent_can_retake_program')}}
              </span>
            </div>
            <div class="setting-right">
              <Switch v-model="settings.isRetry" />
            </div>
          </div>

          <div class="setting-wrapper">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('hr_module')}}
              </span>
              <span class="setting-text">
                {{$t('see_journey_user')}}
              </span>
            </div>
            <div class="setting-right">
              <Switch :model-value="settings.type === '1'" :on-toggle="handleHrToggle"/>
            </div>
          </div>

          <div class="setting-wrapper" v-if="editMode">
            <div class="setting-left">
              <span class="setting-name">
                {{$t('delete_the_program')}}
              </span>
              <span class="setting-text">
                {{$t('permanent_program_deletion')}}
              </span>
            </div>
            <div class="setting-right">
              <button :class="deleteSure ? 'delete-button sure' : 'delete-button'" @click="handleDelete">
                {{ deleteSure ? this.$t('are_you_sure') : this.$t('delete_button') }}
              </button>
            </div>
          </div>

          <div @click="this.onHide" class="close-btn">
            <img class="cross" src="../../assets/images/cross.svg" />
          </div>
        </div>
      </div>
    </div>
  </Transition>
</template>

<script>
import Switch from "@/components/Switch.vue";
import FileDrop from "@/components/FileDrop.vue";
import programService from "../../service/programService";
import {handleErrorResponse} from "@/utils/utils";


export default {
  components: {
    FileDrop,
    Switch,
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    onHide: {
      type: Function,
      default: () => {}
    },

    editMode: {
      type: Boolean,
      default: true
    },

    settings: {
      type: Object,
      default: () => ({
        isRetry: false,
        retryTime: 90,
        startAt: null,
        finishAt: null,
      }),
    },
  },
  data() {
    return {
      startDate: '',
      finishDate: '',
      startSet: false,
      finishSet: false,
      deleteSure: false,
      deleteTimer: null,
      selectedLanguage: null,
      publishedLanguages: {
        tr: false,
        en: false
      }
    }
  },

  created() {
    this.loadLanguageSettings();
  },

  mounted() {
    if (this.settings.isPublish && !this.settings.publishedLanguages) {
      this.settings.publishedLanguages = {
        tr: 0,
        en: 0
      };
    }
  },

  methods: {
    loadLanguageSettings() {
      this.selectedLanguage = null;
      this.publishedLanguages = {
        tr: false,
        en: false
      };

      if (!this.settings.publishedLanguages && this.settings.isPublish) {
        this.settings.publishedLanguages = {
          tr: 0,
          en: 0
        };
      }

      if (this.settings.publishedLanguages) {
        this.publishedLanguages.tr = this.settings.publishedLanguages.tr === 1 ||
            this.settings.publishedLanguages.tr === true;
        this.publishedLanguages.en = this.settings.publishedLanguages.en === 1 ||
            this.settings.publishedLanguages.en === true;

        if (this.publishedLanguages.tr) {
          this.selectedLanguage = 'tr';
        } else if (this.publishedLanguages.en) {
          this.selectedLanguage = 'en';
        } else {
          this.selectedLanguage = null;
        }
      }

      if (this.$route && this.$route.query) {
        if (this.$route.query['publishedLanguages[tr]'] === '1') {
          this.publishedLanguages.tr = true;
          if (!this.selectedLanguage) {
            this.selectedLanguage = 'tr';
          }
        }

        if (this.$route.query['publishedLanguages[en]'] === '1') {
          this.publishedLanguages.en = true;
          if (!this.selectedLanguage && !this.publishedLanguages.tr) {
            this.selectedLanguage = 'en';
          }
        }

        this.updatePublishedLanguages();
      }
    },

    handlePublishChange(value) {
      if (value) {
        if (!this.settings.publishedLanguages) {
          this.settings.publishedLanguages = {
            tr: 0,
            en: 0
          };
        }
        this.updatePublishedLanguages();
      }
    },


    handleLanguageChange(language) {
      if (language === 'tr') {
        this.publishedLanguages.tr = true;
        this.publishedLanguages.en = false;
      } else if (language === 'en') {
        this.publishedLanguages.en = true;
        this.publishedLanguages.tr = false;
      }
      this.updatePublishedLanguages();
    },



    updatePublishedLanguages() {
      if (!this.settings.publishedLanguages) {
        this.settings.publishedLanguages = {};
      }

      this.settings.publishedLanguages.tr = this.publishedLanguages.tr ? 1 : 0;
      this.settings.publishedLanguages.en = this.publishedLanguages.en ? 1 : 0;

      // selectedLanguage güncellemesi - ihtiyaç olursa
      if (this.publishedLanguages.tr) {
        this.selectedLanguage = 'tr';
      } else if (this.publishedLanguages.en) {
        this.selectedLanguage = 'en';
      } else {
        this.selectedLanguage = null;
      }
    },

    handleStartDate(event) {
      this.startSet = true;
      this.settings.startAt = this.formatDate(event.target.value);
    },
    handleFinishDate(event) {
      this.finishSet = true;
      this.settings.finishAt = this.formatDate(event.target.value);
    },
    handleProgramImage(file) {
      this.settings.avatarDelete = false;
      this.settings.avatar = file;
      this.settings.avatarUrl = null
    },
    handleProgramImageDelete() {
      this.settings.avatarDelete = true;
      this.settings.avatar = null;
      this.settings.avatarUrl = null;
    },
    handleHrToggle(value) {
      this.settings.type = value ? '1' : '0';
    },
    formatDate(date) {
      if (!date) return '';
      if (date === '') return '';

      const selectedDate = new Date(date);
      const options = {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      };
      const formatter = new Intl.DateTimeFormat("en-GB", options);
      return formatter.format(selectedDate).replace(/\//g, '.').replace(/[/,]/g, '');
    },
    formattedToIso(inputString) {
      if (!inputString) return '';
      if (inputString === '') return '';

      let datePart, timePart;

      if (inputString.includes('T')) {
        [datePart, timePart] = inputString.split('T');
      } else if (inputString.includes(' ')) {
        [datePart, timePart] = inputString.split(' ');
      } else {
        return '';
      }

      if (!datePart || !timePart) return '';

      let year, month, day;

      if (datePart.includes('.')) {
        [day, month, year] = datePart.split('.');
      } else if (datePart.includes('-')) {
        [year, month, day] = datePart.split('-');
      } else {
        return '';
      }

      const [hours, minutes] = timePart.split(':');

      const formattedDate = new Date(year, month - 1, day, hours, minutes);

      return formattedDate.toISOString().slice(0, 16);
    },

    handleDelete() {
      if (!this.editMode || !this.settings.programId) {
        return;
      }

      if (this.deleteSure) {
        clearTimeout(this.deleteTimer);

        programService.deleteProgram(this.settings.programId).then(() => {
          this.onHide();
          this.$snackbar.add({
            text: this.$t('program_deleted'),
            type: 'warning'
          });

          this.$router.push('/programs');
        }).catch((err) => {
          handleErrorResponse(err, this.$snackbar);
        });
      } else {
        this.deleteSure = true;

        this.deleteTimer = setTimeout(() => {
          this.deleteSure = false;
        }, 3000);
      }
    }
  },

  computed: {
    getStartDate() {
      return this.formattedToIso(this.settings.startAt);
    },
    getFinishDate() {
      return this.formattedToIso(this.settings.finishAt);
    }
  },

  watch: {
    visible(newVal) {
      if (newVal === true) {
        this.loadLanguageSettings();
      }
    },
    'settings.isPublish'(newVal) {
      if (newVal === true && !this.settings.publishedLanguages) {
        this.settings.publishedLanguages = {
          tr: 0,
          en: 0
        };
      }
    },
    'settings.publishedLanguages': {
      deep: true,
      handler(newVal) {
        if (newVal) {
          this.publishedLanguages = {
            tr: newVal.tr === 1 || newVal.tr === true,
            en: newVal.en === 1 || newVal.en === true
          };

          if (this.publishedLanguages.tr) {
            this.selectedLanguage = 'tr';
          } else if (this.publishedLanguages.en) {
            this.selectedLanguage = 'en';
          } else {
            this.selectedLanguage = null;
          }
        }
      }
    }
  }
};
</script>

<style scoped>
.modal {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background-color: rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 24px;
}

.modal-content {
  position: relative;
  background-color: white;
  width: 50%;
  height: 100%;
  border-radius: 12px;
  padding: 24px;
  overflow-y: auto;
  max-height: calc(100vh - 48px);
}

.modal-content::-webkit-scrollbar {
  width: 15px;
}

.modal-content::-webkit-scrollbar-track {
  border-top-right-radius: 12px;
  border-bottom-right-radius: 12px;
  background-color: #FFF;
}

.modal-content::-webkit-scrollbar-thumb {
  background-color: rgba(4, 12, 45, 0.2);
  border-radius: 10px;
  border: 5px transparent solid;
  background-clip: padding-box;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background-color: rgba(4, 12, 45, 0.4);
}

.close-btn {
  position: absolute;
  top: 24px;
  right: 24px;
  background: none;
  border: none;
  cursor: pointer;
  background-color: #F2F4F7;
  border-radius: 3000px;
}
.cross{
  padding: 8px;
}
.modal-header{
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 10px 0 10px 0;
  border: none;
}
.modal-header-txt{
  font-family: euclid_semi_bold, sans-serif;
  color: #040C2D;
  font-size: 18px;
  font-style: normal;
  line-height: 24px;
}
h5 {
  font-family: euclid_medium, sans-serif;
  color: #010511;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  margin-bottom: 10px;
  margin-top: 20px;
}
.modal-header-description {
  font-family: euclid_medium, sans-serif;
  color: #667085;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
}

.setting-wrapper {
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  margin-top: 40px;
}

.setting-left {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 5px;
}

.setting-name {
  font-family: euclid_medium, sans-serif;
  color: #060B27;
  font-size: 18px;
  font-style: normal;
  line-height: 20px;
}

.setting-text {
  color: #667085;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
}

.condition-div span {
  position: absolute;
  display: block;
  font-family: euclid_medium, sans-serif;
  color: #FFF;
  font-size: 14px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) rotate(-90deg);
}

.condition-dropdown img, .range-dropdown img {
  width: 18px;
  cursor: pointer;
}

.condition-dropdown .line, .range-dropdown .line {
  position: absolute;
  left: -12px;
  top: 50%;
  width: 12px;
  transform: translateY(-50%);
  height: 1px;
  background-color: #1f2b3b;
}

.range-dropdown .range-left {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  border-right: 1px solid #e9ecf0;
  padding-right: 20px;
}

.range-left input {
  width: 100%;
  border: none;
  outline: none;
  font-family: euclid_medium, sans-serif;
  color: #667085;
  font-size: 14px;
  font-style: normal;
  background-color: transparent;
  line-height: 20px;
  padding: 0;
}

.range-dropdown .range-right {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding-left: 20px;
}

.delete-button {
  font-family: euclid_regular, sans-serif;
  color: #F44336;
  font-size: 14px;
  font-style: normal;
  line-height: 20px;
  border: 1px solid #F44336;
  background: none;
  cursor: pointer;
  padding: 5px 20px;
  border-radius: 8px;
  transition: all 0.2s;
}

.delete-button:hover {
  background-color: #F44336;
  color: white;
}

.delete-button.sure {
  background-color: #F44336;
  color: white;
  border: 1px solid #F44336;
}

.language-options {
  margin-top: 0;
}

.language-switches {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.language-switch {
  display: flex;
  align-items: center;
  gap: 10px;
}

.language-label {
  font-family: euclid_regular, sans-serif;
  font-size: 14px;
  color: #344054;
}

.language-info-note {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px;
  background: #f3f4f6;
  border-radius: 8px;
  font-size: 12px;
  color: #4b5563;
  margin-top: 12px;
}

.language-info-note svg {
  flex-shrink: 0;
}
</style>
